import React, { createContext, useContext } from 'react';
import { AddModuleOrSubmodule } from './AddModuleOrSubModule';
import { FaPlus } from 'react-icons/fa6';
import { MdEdit } from "react-icons/md";
import { IModuleSchema } from '@/api/module';
import { useDrop } from 'react-dnd';
import { ItemModule } from './ItemModule';
import { RxCross1 } from 'react-icons/rx';
import { ListModuleContext } from '@/pages/module/ListModule';
import { useModuleDispatch, useModuleSelector } from '@/states/moduleHook';
import { updateModuleOrder, updateSubmoduleOrder } from '@/states/moduleSlice';
import { EditModule } from './EditModule';
import { DeleteModule } from './DeleteModule';

type ICardModuleContext = {
  cardIndex?: number;
  onSelectedModule?: (module: IModuleSchema) => void;
  onClose?: () => void;
  onModuleSubmodule?: (
    module: IModuleSchema,
    dragCardIndex: number,
    hoverCardIndex: number,
  ) => void;
};

type CardModuleProps = {
  title: string;
  parent?: IModuleSchema;
  onSuccess: () => void;
};

export const CardModuleContext = createContext<ICardModuleContext>({});

export const CardModule: React.FC<CardModuleProps> = ({ title, parent, onSuccess }) => {
  const { cardIndex } = useContext(CardModuleContext);
  const dispatch = useModuleDispatch();
  const module = useModuleSelector(state => state.value);

  console.log({ module });

  const [, drop] = useDrop({
    accept: 'MODULE',
    drop: (item: { module: IModuleSchema; cardIndex: number }) => {
      const dragCardIndex = item.cardIndex;
      const hoverCardIndex = cardIndex!;

      dispatch(
        updateSubmoduleOrder({
          toLevel: hoverCardIndex,
          fromLevel: dragCardIndex,
          module: item.module,
        }),
      );
    },
  });

  const selectedModule = parent ?? (Array.isArray(module) ? undefined : module);

  return (
    <div
      ref={drop}
      className="flex flex-col h-screen p-4 bg-white border rounded-md shadow-lg border-gray-alurkerja-1 w-80"
    >
      <div className="flex flex-row justify-between">
        <span className="text-sm font-semibold">{title}</span>

        {selectedModule && (
          <CardAction 
            isChild={parent !== undefined}
            onModuleRefetch={onSuccess}
            module={selectedModule}
          />
        )}
      </div>

      <ListModule
        cardIndex={cardIndex!}
        modules={(parent ? parent?.submodule : module) ?? []}
        moveModule={(dragIndex, hoverIndex) =>
          dispatch(
            updateModuleOrder({
              level: cardIndex!,
              dragIndex,
              hoverIndex,
            }),
          )
        }
        isFirstComponent={title.toLowerCase().includes('modules')}
      />
    </div>
  );
};

const CardAction: React.FC<{
  isChild: boolean;
  onModuleRefetch: () => void;
  module: IModuleSchema;
}> = ({ isChild, onModuleRefetch, module }) => {
  const { onClose } = useContext(CardModuleContext);
  const { isViewer, project } = useContext(ListModuleContext);

  const buildEditAction = () => {
    return !isViewer ? (
      <div className="flex gap-2.5">
        <AddModuleOrSubmodule
          project={project!}
          onSuccess={onModuleRefetch}
          triggerButton={
            <button className="mt-1.5">
              <FaPlus size={15} />
            </button>
          }
        />

        <EditModule
          module={module}
          onSuccess={() => {
            // refresh page
            window.location.reload();
          }}
          triggerButton={
            <button className="mt-1.5">
              <MdEdit size={15} />
            </button>
          }
        />

        <DeleteModule
          id={module.id!}
          isArchive={module.is_archive ?? false}
          onSuccess={() => onModuleRefetch?.()}
          onCloseCard={onClose}
        />
      </div>
    ) : null;
  };

  return (
    <div className="flex flex-row gap-2.5">
      {buildEditAction()}

      {isChild && (
        <button className="mb-1" onClick={onClose}>
          <RxCross1 size={15} />
        </button>
      )}
    </div>
  );
};

export const ListModule: React.FC<{
  cardIndex: number;
  modules: IModuleSchema[];
  moveModule: (dragIndex: number, hoverIndex: number) => void;
  isFirstComponent?: boolean;
}> = ({ cardIndex, modules, moveModule, isFirstComponent = true }) => {
  const { onSelectedModule } = useContext(CardModuleContext);
  const [showModule, setShowModule] = React.useState<boolean>(true);

  return (
    <div className="flex flex-col mt-6 gap-2.5 overflow-y-auto max-h-screen">
      <>
        {modules?.[0]?.module_name === 'All Epic' && (
          <ItemModule
            key={modules[0].id}
            module={modules[0]}
            index={0}
            cardIndex={cardIndex}
            moveModule={moveModule}
            onClick={() => setShowModule(!showModule)}
            isParent={true}
          />
        )}

        {showModule &&
          modules
            .filter(module => module.module_name !== 'All Epic')
            .map((module, index) => (
              <ItemModule
                key={module.id}
                module={module}
                index={index + 1}
                cardIndex={cardIndex}
                moveModule={moveModule}
                onClick={() => onSelectedModule?.(module)}
                isParent={!isFirstComponent}
              />
            ))}
      </>
    </div>
  );
};